import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import SafeIcon from "../common/SafeIcon";

import {
  FiMail,
  FiLock,
  FiEye,
  FiEyeOff,
  FiShield,
  FiCheckCircle,
  FiAlertCircle,
  FiUserPlus,
  FiBriefcase,
  FiArrowRight,
  FiArrowLeft,
  FiPhone,
} from "react-icons/fi";

import { useClient } from "../context/ClientContext";
import { INVITATION_STORAGE_KEY } from "./AcceptInvitation";

/* =========================================================
   REGISTRATION OPTIONS
========================================================= */

const ownerTypes = [
  {
    value: "accountant",
    title: "Accountant",
    description:
      "Manage accounting work for your clients.",
  },
  {
    value: "bookkeeper",
    title: "Bookkeeper",
    description:
      "Run bookkeeping workflows for multiple businesses.",
  },
  {
    value: "accounting_firm",
    title: "Accounting Firm",
    description:
      "Operate a branded, multi-client accounting practice.",
  },
];

const initialFormData = {
  workspaceName: "",
  ownerType: "",
  email: "",
  password: "",
  confirmPassword: "",
  firstName: "",
  lastName: "",
  phone: "",
};

const isValidInvitationToken = (value) =>
  typeof value === "string" &&
  /^[0-9a-f]{64}$/i.test(value);

const getPendingInvitation = () => {
  try {
    const token = sessionStorage.getItem(
      INVITATION_STORAGE_KEY
    );

    return isValidInvitationToken(token)
      ? token
      : null;
  } catch {
    return null;
  }
};

const savePendingInvitation = (token) => {
  if (!isValidInvitationToken(token)) return;

  try {
    sessionStorage.setItem(
      INVITATION_STORAGE_KEY,
      token
    );
  } catch {
    // Browser storage may be unavailable.
  }
};

const getErrorMessage = (result, fallback) => {
  if (
    typeof result?.error === "string" &&
    result.error.trim()
  ) {
    return result.error;
  }

  return fallback;
};

/* =========================================================
   SMALL REUSABLE FIELD ERROR COMPONENT
========================================================= */

function FieldError({ name, touched, fieldErrors }) {
  if (!touched[name] || !fieldErrors[name]) {
    return null;
  }

  return (
    <p
      id={`${name}-error`}
      role="alert"
      className="ml-1 text-[10px] font-bold text-rose-500"
    >
      {fieldErrors[name]}
    </p>
  );
}

/* =========================================================
   LOGIN PAGE
========================================================= */

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();

  const {
    login,
    signup,
    resetPassword,
  } = useClient();

  /*
   * An invitation may be stored by AcceptInvitation
   * or supplied in the current URL.
   *
   * HashRouter parses the token into location.search.
   */

  const tokenFromURL = new URLSearchParams(
    location.search
  ).get("token");

  if (isValidInvitationToken(tokenFromURL)) {
    savePendingInvitation(tokenFromURL);
  }

  const pendingClientInvitation =
    isValidInvitationToken(tokenFromURL)
      ? tokenFromURL
      : getPendingInvitation();

  const hasClientInvitation = Boolean(
    pendingClientInvitation
  );

  const invitationPath = pendingClientInvitation
    ? `/accept-invitation?token=${encodeURIComponent(
        pendingClientInvitation
      )}`
    : "/accept-invitation";

  /* -------------------------------------------------------
     AUTHENTICATION FORM STATE
  ------------------------------------------------------- */

  const [mode, setMode] = useState("login");
  const [signupStep, setSignupStep] = useState(1);

  const [showPassword, setShowPassword] =
    useState(false);

  const [
    showConfirmPassword,
    setShowConfirmPassword,
  ] = useState(false);

  const [isLoading, setIsLoading] = useState(false);

  const [error, setError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");

  const [touched, setTouched] = useState({});
  const [fieldErrors, setFieldErrors] = useState({});

  const [formData, setFormData] = useState(
    initialFormData
  );

  /* -------------------------------------------------------
     FIELD VALIDATION
  ------------------------------------------------------- */

  const validateField = (
    name,
    value,
    data = formData
  ) => {
    const text =
      typeof value === "string" ? value : "";

    if (name === "workspaceName") {
      if (!text.trim()) {
        return "Business or firm name is required.";
      }

      if (text.trim().length < 2) {
        return "Enter at least two characters.";
      }
    }

    if (name === "ownerType") {
      if (
        !ownerTypes.some(
          (type) => type.value === text
        )
      ) {
        return "Choose Accountant, Bookkeeper, or Accounting Firm.";
      }
    }

    if (name === "firstName" && mode === "signup") {
      if (!text.trim()) {
        return "First name is required.";
      }
    }

    if (name === "lastName" && mode === "signup") {
      if (!text.trim()) {
        return "Last name is required.";
      }
    }

    if (name === "email") {
      if (!text.trim()) {
        return "Email is required.";
      }

      if (
        !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(
          text.trim()
        )
      ) {
        return "Enter a valid email address.";
      }
    }

    if (
      name === "password" &&
      mode !== "forgot-password"
    ) {
      if (!text) {
        return "Password is required.";
      }

      if (mode === "signup") {
        if (text.length < 8) {
          return "Password must be at least 8 characters.";
        }

        if (
          !/(?=.*[0-9])(?=.*[a-z])(?=.*[A-Z])/.test(
            text
          )
        ) {
          return "Use uppercase, lowercase, and a number.";
        }
      }
    }

    if (
      name === "confirmPassword" &&
      mode === "signup"
    ) {
      if (!text) {
        return "Please confirm your password.";
      }

      if (text !== data.password) {
        return "Passwords do not match.";
      }
    }

    return "";
  };

  /* -------------------------------------------------------
     INPUT EVENTS
  ------------------------------------------------------- */

  const handleInputChange = (event) => {
    const { name, value } = event.target;

    const nextData = {
      ...formData,
      [name]: value,
    };

    setFormData(nextData);

    setError("");
    setSuccessMsg("");

    if (touched[name]) {
      setFieldErrors((previous) => ({
        ...previous,
        [name]: validateField(
          name,
          value,
          nextData
        ),
      }));
    }

    if (
      name === "password" &&
      touched.confirmPassword
    ) {
      setFieldErrors((previous) => ({
        ...previous,
        confirmPassword: validateField(
          "confirmPassword",
          nextData.confirmPassword,
          nextData
        ),
      }));
    }
  };

  const handleBlur = (event) => {
    const { name, value } = event.target;

    setTouched((previous) => ({
      ...previous,
      [name]: true,
    }));

    setFieldErrors((previous) => ({
      ...previous,
      [name]: validateField(name, value),
    }));
  };

  const selectOwnerType = (value) => {
    setFormData((previous) => ({
      ...previous,
      ownerType: value,
    }));

    setTouched((previous) => ({
      ...previous,
      ownerType: true,
    }));

    setFieldErrors((previous) => ({
      ...previous,
      ownerType: "",
    }));

    setError("");
  };

  /* -------------------------------------------------------
     FORM VALIDATION
  ------------------------------------------------------- */

  const validateForm = () => {
    let fields = [];

    if (mode === "login") {
      fields = ["email", "password"];
    } else if (mode === "forgot-password") {
      fields = ["email"];
    } else if (signupStep === 1) {
      fields = ["workspaceName", "ownerType"];
    } else {
      fields = [
        "firstName",
        "lastName",
        "email",
        "password",
        "confirmPassword",
      ];
    }

    const errors = {};

    fields.forEach((field) => {
      const message = validateField(
        field,
        formData[field]
      );

      if (message) {
        errors[field] = message;
      }
    });

    setFieldErrors(errors);

    setTouched((previous) => ({
      ...previous,
      ...Object.fromEntries(
        fields.map((field) => [field, true])
      ),
    }));

    return Object.keys(errors).length === 0;
  };

  /* -------------------------------------------------------
     LOGIN, FIRM SIGNUP, PASSWORD RESET
  ------------------------------------------------------- */

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (isLoading) return;

    setError("");
    setSuccessMsg("");

    if (!validateForm()) return;

    if (
      mode === "signup" &&
      signupStep === 1
    ) {
      setSignupStep(2);
      return;
    }

    setIsLoading(true);

    try {
      /*
       * LOGIN
       */

      if (mode === "login") {
        const result = await login(
          formData.email.trim(),
          formData.password
        );

        if (!result?.success) {
          setError(
            getErrorMessage(
              result,
              "Unable to sign in. Please try again."
            )
          );
          return;
        }

        /*
         * IMPORTANT:
         * When an invitation is pending, return
         * to AcceptInvitation rather than the
         * accounting dashboard.
         *
         * ClientContext and the invitation
         * acceptance backend will handle
         * authorization and workspace loading.
         */

        const savedToken =
          getPendingInvitation() ||
          pendingClientInvitation;

        if (isValidInvitationToken(savedToken)) {
          navigate(
            `/accept-invitation?token=${encodeURIComponent(
              savedToken
            )}`,
            { replace: true }
          );
        } else {
          navigate("/", { replace: true });
        }

        return;
      }

      /*
       * ACCOUNTANT / BOOKKEEPER / FIRM SIGNUP
       *
       * This existing signup is deliberately
       * separate from invited-client registration.
       */

      if (mode === "signup") {
        const result = await signup({
          workspaceName:
            formData.workspaceName.trim(),

          ownerType: formData.ownerType,

          email:
            formData.email.trim().toLowerCase(),

          password: formData.password,

          firstName:
            formData.firstName.trim(),

          lastName:
            formData.lastName.trim(),

          phone: formData.phone.trim(),

          fullName: [
            formData.firstName.trim(),
            formData.lastName.trim(),
          ]
            .filter(Boolean)
            .join(" "),
        });

        if (!result?.success) {
          setError(
            getErrorMessage(
              result,
              "Unable to create your account."
            )
          );
          return;
        }

        if (result.data?.session) {
          navigate("/", { replace: true });
          return;
        }

        setSuccessMsg(
          "Your registration request was received. " +
          "If email verification is required, " +
          "check your inbox for a confirmation link. " +
          "After verification, return here to sign in."
        );

        setFormData((previous) => ({
          ...previous,
          password: "",
          confirmPassword: "",
        }));

        setShowPassword(false);
        setShowConfirmPassword(false);

        return;
      }

      /*
       * FORGOT PASSWORD
       */

      if (mode === "forgot-password") {
        const result = await resetPassword(
          formData.email.trim()
        );

        if (result?.success) {
          setSuccessMsg(
            "If an account exists for this email, " +
            "you will receive password recovery instructions."
          );
        } else {
          setError(
            getErrorMessage(
              result,
              "Unable to process your request."
            )
          );
        }
      }
    } catch (requestError) {
      console.error(
        "ProfitYo authentication form error:",
        requestError
      );

      setError(
        "Something went wrong. Please try again."
      );
    } finally {
      setIsLoading(false);
    }
  };

  /* -------------------------------------------------------
     MODE SWITCHING
  ------------------------------------------------------- */

  const toggleMode = (nextMode) => {
    if (isLoading) return;

    /*
     * Invited clients must never enter the
     * accounting firm registration flow.
     */

    if (
      nextMode === "signup" &&
      hasClientInvitation
    ) {
      navigate("/client-register");
      return;
    }

    setMode(nextMode);
    setSignupStep(1);

    setError("");
    setSuccessMsg("");

    setTouched({});
    setFieldErrors({});

    setShowPassword(false);
    setShowConfirmPassword(false);

    setFormData((previous) => ({
      ...previous,
      password: "",
      confirmPassword: "",
    }));
  };

  const handleCreateAccount = () => {
    if (isLoading) return;

    if (hasClientInvitation) {
      navigate("/client-register");
      return;
    }

    toggleMode("signup");
  };

  /* -------------------------------------------------------
     FIELD STYLES
  ------------------------------------------------------- */

  const inputClass = (field) =>
    `w-full px-5 py-3.5 bg-white border-2 rounded-2xl text-sm font-black outline-none transition-all ${
      touched[field] && fieldErrors[field]
        ? "border-rose-300 bg-rose-50/50 focus:border-rose-400"
        : "border-slate-100 focus:border-emerald-500 focus:ring-4 focus:ring-emerald-500/10"
    }`;

  const errorProps = (field) => ({
    "aria-invalid": Boolean(
      touched[field] && fieldErrors[field]
    ),
    "aria-describedby":
      touched[field] && fieldErrors[field]
        ? `${field}-error`
        : undefined,
  });

  /* =========================================================
     PAGE UI
  ========================================================= */

  return (
    <div className="flex min-h-screen overflow-hidden bg-white font-sans">

      {/* LEFT MARKETING PANEL */}

      <div className="relative hidden items-center justify-center overflow-hidden bg-slate-900 p-12 lg:flex lg:w-1/2">
        <div className="relative z-10 max-w-lg">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="mb-12 flex items-center gap-4">
              <div className="flex h-14 w-14 rotate-3 items-center justify-center rounded-2xl bg-emerald-500 shadow-2xl">
                <SafeIcon
                  icon={FiShield}
                  className="text-2xl text-white"
                />
              </div>

              <span className="text-3xl font-black tracking-tighter text-white">
                ProfitYo
              </span>
            </div>

            <h1 className="mb-6 text-5xl font-black leading-tight tracking-tight text-white">
              Built for Accountants.
              <br />
              <span className="text-emerald-500">
                Powered for Their Clients.
              </span>
            </h1>

            <p className="mb-12 text-lg font-medium leading-relaxed text-slate-400">
              Manage multiple clients, streamline
              bookkeeping, collaborate securely,
              and provide branded client portals
              with AI-powered financial insights.
            </p>

            <div className="space-y-6">
              {[
                [
                  "Multi-Client Management",
                  "Manage all client businesses from a single workspace.",
                ],
                [
                  "White-Label Client Portal",
                  "Deliver a fully branded accounting experience to your clients.",
                ],
                [
                  "AI Financial Assistant",
                  "Help clients understand transactions, reports, and financial performance instantly.",
                ],
                [
                  "Secure Accounting Workspace",
                  "Role-based access, audit trails, and complete client data isolation.",
                ],
              ].map(([title, description]) => (
                <div
                  key={title}
                  className="flex items-start gap-4 rounded-2xl border border-white/5 bg-white/[0.02] p-4"
                >
                  <div className="mt-1 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-emerald-500/20">
                    <SafeIcon
                      icon={FiCheckCircle}
                      className="text-sm text-emerald-500"
                    />
                  </div>

                  <div>
                    <h4 className="text-sm font-black text-white">
                      {title}
                    </h4>

                    <p className="mt-1 text-xs font-bold text-slate-500">
                      {description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>

        <div className="absolute right-[-10%] top-[-10%] h-96 w-96 rounded-full bg-emerald-500/10 blur-[120px]" />
        <div className="absolute bottom-[-5%] left-[-5%] h-80 w-80 rounded-full bg-blue-600/10 blur-[100px]" />
      </div>

      {/* RIGHT AUTHENTICATION PANEL */}

      <div className="relative flex w-full items-center justify-center overflow-y-auto bg-[#f8fafc] p-8 md:p-16 lg:w-1/2 lg:p-24">
        <div className="w-full max-w-md">
          <AnimatePresence mode="wait">
            <motion.div
              key={`${mode}-${signupStep}`}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
            >
              <div className="mb-10">
                <div className="mb-4 inline-flex items-center gap-2 rounded-full bg-emerald-50 px-3 py-1 text-[10px] font-black uppercase tracking-widest text-emerald-700">
                  <SafeIcon icon={FiShield} />
                  Secure Access
                </div>

                <h2 className="mb-3 text-4xl font-black tracking-tight text-slate-900">
                  {mode === "login" &&
                    (hasClientInvitation
                      ? "Welcome to ProfitYo"
                      : "Welcome Back")}

                  {mode === "signup" &&
                    signupStep === 1 &&
                    "Create Your Accounting Workspace"}

                  {mode === "signup" &&
                    signupStep === 2 &&
                    "Create Your Account"}

                  {mode === "forgot-password" &&
                    "Reset Password"}
                </h2>

                <p className="text-sm font-bold text-slate-500">
                  {mode === "login" &&
                    (hasClientInvitation
                      ? "Sign in with the email address that received your client invitation."
                      : "Access your professional financial command center.")}

                  {mode === "signup" &&
                    signupStep === 1 &&
                    "Start by entering your business or firm name."}

                  {mode === "signup" &&
                    signupStep === 2 &&
                    "Enter your details to create your account and firm workspace."}

                  {mode === "forgot-password" &&
                    "Enter your email to receive recovery instructions."}
                </p>
              </div>

              {/* PENDING CLIENT INVITATION BANNER */}

              {hasClientInvitation &&
                mode === "login" && (
                  <div className="mb-6 flex items-start gap-3 rounded-2xl border border-emerald-200 bg-emerald-50 p-4">
                    <SafeIcon
                      icon={FiCheckCircle}
                      className="mt-0.5 shrink-0 text-lg text-emerald-600"
                    />

                    <div>
                      <p className="text-sm font-black text-emerald-900">
                        Client Invitation
                      </p>

                      <p className="mt-1 text-xs font-medium leading-relaxed text-emerald-800">
                        Your invitation is saved.
                        Sign in to continue, or create
                        a client account if you are new
                        to ProfitYo.
                      </p>

                      <button
                        type="button"
                        onClick={() =>
                          navigate(invitationPath)
                        }
                        className="mt-3 text-xs font-bold text-emerald-700 underline"
                      >
                        Back to invitation
                      </button>
                    </div>
                  </div>
                )}

              {/* SIGNUP PROGRESS */}

              {mode === "signup" && (
                <div className="mb-8">
                  <div className="mb-2 flex justify-between text-[10px] font-black uppercase tracking-widest text-slate-400">
                    <span>
                      Step {signupStep} of 2
                    </span>

                    <span className="text-emerald-600">
                      {signupStep === 1
                        ? "Workspace"
                        : "Account"}
                    </span>
                  </div>

                  <div className="h-2 overflow-hidden rounded-full bg-slate-200">
                    <div
                      className="h-full rounded-full bg-emerald-500 transition-all"
                      style={{
                        width: `${signupStep * 50}%`,
                      }}
                    />
                  </div>
                </div>
              )}

              {/* ERROR MESSAGE */}

              {error && (
                <div
                  role="alert"
                  className="mb-6 flex items-start gap-3 rounded-2xl border border-rose-100 bg-rose-50 p-4 text-xs font-black text-rose-700"
                >
                  <SafeIcon
                    icon={FiAlertCircle}
                    className="mt-0.5 shrink-0 text-sm"
                  />

                  <div>
                    <p className="mb-1 uppercase tracking-wider">
                      Unable to Continue
                    </p>

                    <p className="font-bold leading-relaxed opacity-80">
                      {error}
                    </p>
                  </div>
                </div>
              )}

              {/* SUCCESS MESSAGE */}

              {successMsg && (
                <div
                  role="status"
                  className="mb-6 flex items-start gap-3 rounded-2xl border border-emerald-100 bg-emerald-50 p-4 text-xs font-black text-emerald-700"
                >
                  <SafeIcon
                    icon={FiCheckCircle}
                    className="mt-0.5 shrink-0 text-sm"
                  />

                  <p className="font-bold leading-relaxed opacity-80">
                    {successMsg}
                  </p>
                </div>
              )}

              {/* AUTHENTICATION FORM */}

              <form
                onSubmit={handleSubmit}
                className="space-y-5"
                noValidate
              >
                {/* FIRM SIGNUP STEP 1 */}

                {mode === "signup" &&
                  signupStep === 1 && (
                    <>
                      <div className="space-y-1.5">
                        <label
                          htmlFor="workspaceName"
                          className="flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-slate-400"
                        >
                          <SafeIcon icon={FiBriefcase} />
                          Business / Firm Name
                        </label>

                        <input
                          id="workspaceName"
                          autoFocus
                          type="text"
                          name="workspaceName"
                          autoComplete="organization"
                          maxLength={160}
                          value={formData.workspaceName}
                          onChange={handleInputChange}
                          onBlur={handleBlur}
                          {...errorProps("workspaceName")}
                          className={inputClass(
                            "workspaceName"
                          )}
                          placeholder="ABC Accounting Firm"
                        />

                        <FieldError
                          name="workspaceName"
                          touched={touched}
                          fieldErrors={fieldErrors}
                        />
                      </div>

                      <fieldset className="space-y-3">
                        <legend className="text-[10px] font-black uppercase tracking-widest text-slate-400">
                          I am registering as
                        </legend>

                        <div className="space-y-3">
                          {ownerTypes.map((type) => (
                            <button
                              type="button"
                              key={type.value}
                              disabled={isLoading}
                              aria-pressed={
                                formData.ownerType ===
                                type.value
                              }
                              onClick={() =>
                                selectOwnerType(
                                  type.value
                                )
                              }
                              className={`w-full rounded-2xl border-2 p-4 text-left transition-all ${
                                formData.ownerType ===
                                type.value
                                  ? "border-emerald-500 bg-emerald-50"
                                  : "border-slate-100 bg-white hover:border-emerald-200"
                              }`}
                            >
                              <div className="flex items-center justify-between">
                                <span className="text-sm font-black text-slate-900">
                                  {type.title}
                                </span>

                                {formData.ownerType ===
                                  type.value && (
                                  <SafeIcon
                                    icon={FiCheckCircle}
                                    className="text-emerald-600"
                                  />
                                )}
                              </div>

                              <p className="mt-1 text-xs font-bold text-slate-500">
                                {type.description}
                              </p>
                            </button>
                          ))}
                        </div>

                        <FieldError
                          name="ownerType"
                          touched={touched}
                          fieldErrors={fieldErrors}
                        />
                      </fieldset>

                      <p className="rounded-2xl border border-emerald-100 bg-emerald-50 p-4 text-xs font-bold leading-relaxed text-emerald-800">
                        Examples: ABC Accounting Firm,
                        Smith Bookkeeping Services,
                        or Johnson Construction LLC.
                      </p>

                      <button
                        type="submit"
                        disabled={isLoading}
                        className="flex w-full items-center justify-center gap-3 rounded-2xl bg-slate-900 py-4 text-xs font-black uppercase tracking-[0.2em] text-white shadow-xl transition-all hover:bg-black disabled:opacity-70"
                      >
                        Continue
                        <SafeIcon icon={FiArrowRight} />
                      </button>
                    </>
                  )}

                {/* FIRM SIGNUP STEP 2 */}

                {mode === "signup" &&
                  signupStep === 2 && (
                    <>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-1.5">
                          <label
                            htmlFor="firstName"
                            className="text-[10px] font-black uppercase tracking-widest text-slate-400"
                          >
                            First Name
                          </label>

                          <input
                            id="firstName"
                            type="text"
                            name="firstName"
                            autoComplete="given-name"
                            value={formData.firstName}
                            onChange={handleInputChange}
                            onBlur={handleBlur}
                            className={inputClass(
                              "firstName"
                            )}
                            placeholder="John"
                            {...errorProps("firstName")}
                          />

                          <FieldError
                            name="firstName"
                            touched={touched}
                            fieldErrors={fieldErrors}
                          />
                        </div>

                        <div className="space-y-1.5">
                          <label
                            htmlFor="lastName"
                            className="text-[10px] font-black uppercase tracking-widest text-slate-400"
                          >
                            Last Name
                          </label>

                          <input
                            id="lastName"
                            type="text"
                            name="lastName"
                            autoComplete="family-name"
                            value={formData.lastName}
                            onChange={handleInputChange}
                            onBlur={handleBlur}
                            className={inputClass(
                              "lastName"
                            )}
                            placeholder="Doe"
                            {...errorProps("lastName")}
                          />

                          <FieldError
                            name="lastName"
                            touched={touched}
                            fieldErrors={fieldErrors}
                          />
                        </div>
                      </div>

                      <div className="space-y-1.5">
                        <label
                          htmlFor="signupEmail"
                          className="flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-slate-400"
                        >
                          <SafeIcon icon={FiMail} />
                          Work Email
                        </label>

                        <input
                          id="signupEmail"
                          type="email"
                          name="email"
                          autoComplete="username"
                          inputMode="email"
                          spellCheck={false}
                          autoCapitalize="none"
                          value={formData.email}
                          onChange={handleInputChange}
                          onBlur={handleBlur}
                          className={inputClass("email")}
                          placeholder="name@company.com"
                          {...errorProps("email")}
                        />

                        <FieldError
                          name="email"
                          touched={touched}
                          fieldErrors={fieldErrors}
                        />
                      </div>

                      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                        <div className="space-y-1.5">
                          <label
                            htmlFor="signupPassword"
                            className="flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-slate-400"
                          >
                            <SafeIcon icon={FiLock} />
                            Password
                          </label>

                          <div className="relative">
                            <input
                              id="signupPassword"
                              type={
                                showPassword
                                  ? "text"
                                  : "password"
                              }
                              name="password"
                              autoComplete="new-password"
                              minLength={8}
                              value={formData.password}
                              onChange={handleInputChange}
                              onBlur={handleBlur}
                              className={`${inputClass(
                                "password"
                              )} pr-12`}
                              placeholder="••••••••"
                              {...errorProps("password")}
                            />

                            <button
                              type="button"
                              aria-label={
                                showPassword
                                  ? "Hide password"
                                  : "Show password"
                              }
                              onClick={() =>
                                setShowPassword(
                                  (value) => !value
                                )
                              }
                              className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400"
                            >
                              <SafeIcon
                                icon={
                                  showPassword
                                    ? FiEyeOff
                                    : FiEye
                                }
                              />
                            </button>
                          </div>

                          <FieldError
                            name="password"
                            touched={touched}
                            fieldErrors={fieldErrors}
                          />
                        </div>

                        <div className="space-y-1.5">
                          <label
                            htmlFor="confirmPassword"
                            className="text-[10px] font-black uppercase tracking-widest text-slate-400"
                          >
                            Confirm Password
                          </label>

                          <div className="relative">
                            <input
                              id="confirmPassword"
                              type={
                                showConfirmPassword
                                  ? "text"
                                  : "password"
                              }
                              name="confirmPassword"
                              autoComplete="new-password"
                              value={
                                formData.confirmPassword
                              }
                              onChange={handleInputChange}
                              onBlur={handleBlur}
                              className={`${inputClass(
                                "confirmPassword"
                              )} pr-12`}
                              placeholder="••••••••"
                              {...errorProps(
                                "confirmPassword"
                              )}
                            />

                            <button
                              type="button"
                              aria-label={
                                showConfirmPassword
                                  ? "Hide confirmation password"
                                  : "Show confirmation password"
                              }
                              onClick={() =>
                                setShowConfirmPassword(
                                  (value) => !value
                                )
                              }
                              className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400"
                            >
                              <SafeIcon
                                icon={
                                  showConfirmPassword
                                    ? FiEyeOff
                                    : FiEye
                                }
                              />
                            </button>
                          </div>

                          <FieldError
                            name="confirmPassword"
                            touched={touched}
                            fieldErrors={fieldErrors}
                          />
                        </div>
                      </div>

                      <div className="space-y-1.5">
                        <label
                          htmlFor="signupPhone"
                          className="flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-slate-400"
                        >
                          <SafeIcon icon={FiPhone} />
                          Phone Number
                        </label>

                        <input
                          id="signupPhone"
                          type="tel"
                          name="phone"
                          autoComplete="tel"
                          value={formData.phone}
                          onChange={handleInputChange}
                          className={inputClass("phone")}
                          placeholder="+1 (555) 000-0000"
                        />
                      </div>

                      <div className="flex gap-4">
                        <button
                          type="button"
                          disabled={isLoading}
                          onClick={() =>
                            setSignupStep(1)
                          }
                          className="flex flex-1 items-center justify-center gap-2 rounded-2xl border-2 border-slate-200 py-4 text-xs font-black uppercase tracking-widest text-slate-600 disabled:opacity-70"
                        >
                          <SafeIcon icon={FiArrowLeft} />
                          Back
                        </button>

                        <button
                          type="submit"
                          disabled={isLoading}
                          className="flex-[2] rounded-2xl bg-emerald-600 py-4 text-xs font-black uppercase tracking-widest text-white shadow-xl transition-all hover:bg-emerald-700 disabled:opacity-70"
                        >
                          {isLoading
                            ? "Creating Account..."
                            : "Create Account"}
                        </button>
                      </div>
                    </>
                  )}

                {/* LOGIN / FORGOT PASSWORD */}

                {mode !== "signup" && (
                  <>
                    <div className="space-y-1.5">
                      <label
                        htmlFor="loginEmail"
                        className="flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-slate-400"
                      >
                        <SafeIcon icon={FiMail} />
                        Email Address
                      </label>

                      <input
                        id="loginEmail"
                        type="email"
                        name="email"
                        autoComplete={
                          mode === "login"
                            ? "username"
                            : "email"
                        }
                        inputMode="email"
                        autoCapitalize="none"
                        spellCheck={false}
                        value={formData.email}
                        onChange={handleInputChange}
                        onBlur={handleBlur}
                        className={inputClass("email")}
                        placeholder="name@company.com"
                        {...errorProps("email")}
                      />

                      <FieldError
                        name="email"
                        touched={touched}
                        fieldErrors={fieldErrors}
                      />
                    </div>

                    {mode === "login" && (
                      <div className="space-y-1.5">
                        <div className="ml-1 flex items-center justify-between">
                          <label
                            htmlFor="loginPassword"
                            className="flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-slate-400"
                          >
                            <SafeIcon icon={FiLock} />
                            Password
                          </label>

                          <button
                            type="button"
                            onClick={() =>
                              toggleMode(
                                "forgot-password"
                              )
                            }
                            className="text-[10px] font-black uppercase tracking-wider text-emerald-600"
                          >
                            Forgot?
                          </button>
                        </div>

                        <div className="relative">
                          <input
                            id="loginPassword"
                            type={
                              showPassword
                                ? "text"
                                : "password"
                            }
                            name="password"
                            autoComplete="current-password"
                            value={formData.password}
                            onChange={handleInputChange}
                            onBlur={handleBlur}
                            className={`${inputClass(
                              "password"
                            )} pr-12`}
                            placeholder="••••••••"
                            {...errorProps("password")}
                          />

                          <button
                            type="button"
                            aria-label={
                              showPassword
                                ? "Hide password"
                                : "Show password"
                            }
                            onClick={() =>
                              setShowPassword(
                                (value) => !value
                              )
                            }
                            className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400"
                          >
                            <SafeIcon
                              icon={
                                showPassword
                                  ? FiEyeOff
                                  : FiEye
                              }
                            />
                          </button>
                        </div>

                        <FieldError
                          name="password"
                          touched={touched}
                          fieldErrors={fieldErrors}
                        />
                      </div>
                    )}

                    <button
                      type="submit"
                      disabled={isLoading}
                      className="w-full rounded-2xl bg-slate-900 py-4 text-xs font-black uppercase tracking-[0.2em] text-white shadow-xl transition-all hover:bg-black disabled:opacity-70"
                    >
                      {isLoading
                        ? "Processing..."
                        : mode === "login"
                        ? hasClientInvitation
                          ? "Sign In & Continue"
                          : "Sign In"
                        : "Reset Password"}
                    </button>
                  </>
                )}
              </form>

              {/* CREATE ACCOUNT */}

              {mode === "login" && (
                <div className="mt-10 flex flex-col items-center justify-between gap-4 border-t border-slate-100 pt-8 sm:flex-row">
                  <p className="text-sm font-bold text-slate-500">
                    {hasClientInvitation
                      ? "New to ProfitYo?"
                      : "Don't have an account?"}
                  </p>

                  <button
                    type="button"
                    onClick={handleCreateAccount}
                    disabled={isLoading}
                    className="rounded-xl bg-emerald-500 px-6 py-2.5 text-[10px] font-black uppercase tracking-widest text-white shadow-lg transition-colors hover:bg-emerald-600 disabled:opacity-70"
                  >
                    <SafeIcon
                      icon={FiUserPlus}
                      className="mr-2 inline"
                    />

                    {hasClientInvitation
                      ? "Create Client Account"
                      : "Create Account"}
                  </button>
                </div>
              )}

              {/* BACK TO LOGIN */}

              {mode !== "login" && (
                <div className="mt-10 border-t border-slate-100 pt-8 text-center">
                  <button
                    type="button"
                    onClick={() =>
                      toggleMode("login")
                    }
                    className="text-sm font-black text-emerald-600"
                  >
                    Back to Sign In
                  </button>
                </div>
              )}

            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}